'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AdBanner from '@/components/AdBanner';
import BoardList from '@/components/BoardList';
import { ListingCard } from '@/app/components/MarketplaceListingsScreen';
import { GRAIN_FEED_LABEL } from '@/lib/features';
import { GRAIN_FEED_GROUPS } from '@/lib/grain-feed';
import { createClient } from '@/lib/supabase/client';
import { fetchListings, type Listing } from '@/lib/listings';

const PAGE_SIZE = 24;

/**
 * Grain, Feed & Bedding.
 *
 * Members post here now — the sector used to be an admin-curated board only,
 * which is why there was no way to list a load of barley. Real listings come
 * first, filtered by the headings in src/lib/grain-feed.ts; anything the
 * FarmTradeDirect team has added by hand follows underneath, and that section
 * disappears entirely when there is nothing in it.
 */
export default function CropFeedBeddingScreen() {
  const supabase = useMemo(() => createClient(), []);
  const [group, setGroup] = useState<string>('all');
  const [listings, setListings] = useState<Listing[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { listings: rows, total: count } = await fetchListings(supabase, {
        category: 'crops-feed',
        subCategory: group === 'all' ? undefined : group,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setListings(rows);
      setTotal(count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load these listings.');
      setListings([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [supabase, group, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [group]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-foreground">{GRAIN_FEED_LABEL}</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl leading-relaxed">
            Grain and arable crops, forage, straights, compound feed, minerals and bedding &mdash;
            straw, hay, haylage and silage. Direct from farm, no commission on the sale.
          </p>
        </div>
        <Link href="/post-listing" className="btn-accent self-start md:self-auto shrink-0">
          <Icon name="PlusCircleIcon" size={18} />
          Post a Listing
        </Link>
      </div>

      {/* Category filter — the headings a seller picks from when posting */}
      <div className="flex gap-2 flex-wrap mb-6">
        <button
          type="button"
          onClick={() => setGroup('all')}
          className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${
            group === 'all'
              ? 'bg-primary text-white border-primary'
              : 'bg-card text-foreground border-border hover:border-primary/50'
          }`}
        >
          All categories
        </button>
        {GRAIN_FEED_GROUPS.map((g) => (
          <button
            key={g.value}
            type="button"
            onClick={() => setGroup(g.value)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-all ${
              group === g.value
                ? 'bg-primary text-white border-primary'
                : 'bg-card text-foreground border-border hover:border-primary/50'
            }`}
          >
            <Icon name={g.icon} size={14} />
            {g.label}
          </button>
        ))}
      </div>

      {/* Listings */}
      {error ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center" role="alert">
          <Icon name="ExclamationTriangleIcon" size={40} className="text-red-500 mb-3" />
          <h3 className="text-lg font-bold text-foreground mb-1">Couldn&apos;t load listings</h3>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <button type="button" onClick={load} className="btn-primary">Try again</button>
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`sk-${i}`} className="card p-0 overflow-hidden border border-border animate-pulse">
              <div className="h-44 bg-muted" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : listings.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-16 text-center">
          <Icon name="GlobeEuropeAfricaIcon" size={40} className="text-muted-foreground mb-3" />
          <h3 className="text-lg font-bold text-foreground mb-1">
            {group === 'all' ? 'Nothing listed here yet' : 'Nothing in that category yet'}
          </h3>
          <p className="text-muted-foreground text-sm mb-4 max-w-md">
            Grain, forage, feed and bedding are listed by members direct from farm. Be the first —
            it takes a couple of minutes.
          </p>
          <Link href="/post-listing" className="btn-primary">Post a Listing</Link>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            Showing <span className="font-bold text-foreground">{listings.length}</span>
            {total > listings.length && <> of <span className="font-bold text-foreground">{total}</span></>}
            {' '}listing{total === 1 ? '' : 's'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {listings.map((listing) => <ListingCard key={listing.id} listing={listing} />)}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="btn-secondary text-sm py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronLeftIcon" size={16} />
                Previous
              </button>
              <span className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="btn-secondary text-sm py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <Icon name="ChevronRightIcon" size={16} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Anything the FarmTradeDirect team has listed on behalf of a seller.
          Renders nothing when the board is empty. */}
      <section className="mt-12">
        <BoardList
          board="crops-feed-bedding"
          emptyTitle=""
          emptyMessage=""
          enquiryLabel="Enquire about this"
          hideWhenEmpty
          heading="Also available through FarmTradeDirect"
          headingBlurb="Loads we are handling directly on behalf of the seller."
        />
      </section>

      <div className="mt-10">
        <AdBanner width={728} height={90} label="728x90 Leaderboard Banner" className="mx-auto rounded-lg" />
      </div>
    </main>
  );
}
